import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  approveSoundbedRights,
  enqueueInternalSoundbedReview,
  fetchAndApproveSoundbedSource,
  fetchLockedSoundbedRegistry,
  fetchSoundbedAssignmentOptions,
  fetchSoundbedAssignments,
  fetchInternalSoundbedReviews,
  fetchSoundbedReviews,
  intakeLockedSoundbed,
  LockedSoundbedAsset,
  InternalSoundbedReview,
  reviseSoundbedAssignmentMixProfile,
  SoundbedAssignment,
  SoundbedAssignmentOption,
  SoundbedReviewAsset,
  stageSoundbedAssignment,
} from '../../api/soundbedAdminApi';
import { fetchVoiceListeners, Listener } from '../../api/listenersApi';
import { useApp } from '../../context/AppContext';
import './SoundbedAdmin.css';

const ROLLOUT_REFERENCE = 'gate15-phase-g-user-approved-2026-08-08';

const SoundbedAdmin: React.FC = () => {
  const { state, fetchVoices } = useApp();
  const [registry, setRegistry] = useState<Awaited<ReturnType<typeof fetchLockedSoundbedRegistry>> | null>(null);
  const [reviews, setReviews] = useState<SoundbedReviewAsset[]>([]);
  const [assignmentOptions, setAssignmentOptions] = useState<SoundbedAssignmentOption[]>([]);
  const [assignments, setAssignments] = useState<SoundbedAssignment[]>([]);
  const [canaryRenders, setCanaryRenders] = useState<InternalSoundbedReview[]>([]);
  const [queuedAssignmentIds, setQueuedAssignmentIds] = useState<string[]>([]);
  const [approvalReference, setApprovalReference] = useState(ROLLOUT_REFERENCE);
  const [rightsConfirmed, setRightsConfirmed] = useState<Record<string, boolean>>({});
  const [selectedStoryId, setSelectedStoryId] = useState('');
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [selectedListenerId, setSelectedListenerId] = useState('');
  const [voiceListeners, setVoiceListeners] = useState<Listener[]>([]);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [nextRegistry, nextReviews, nextOptions, nextAssignments, nextCanaries] = await Promise.all([
        fetchLockedSoundbedRegistry(),
        fetchSoundbedReviews(),
        fetchSoundbedAssignmentOptions(),
        fetchSoundbedAssignments(),
        fetchInternalSoundbedReviews(),
      ]);
      setRegistry(nextRegistry);
      setReviews(nextReviews);
      setAssignmentOptions(nextOptions);
      setAssignments(nextAssignments);
      setCanaryRenders(nextCanaries);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load soundbed review.');
    }
  }, []);

  const refreshCanaries = useCallback(async () => {
    const next = await fetchInternalSoundbedReviews();
    setCanaryRenders(next);
    setQueuedAssignmentIds((queued) => queued.filter(
      (assignmentId) => !next.some((render) => render.assignment_id === assignmentId)
    ));
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (!state.voices.length) void fetchVoices(); }, [fetchVoices, state.voices.length]);

  const chooseVoice = async (voiceId: string) => {
    setSelectedVoiceId(voiceId);
    setSelectedListenerId('');
    setVoiceListeners([]);
    if (!voiceId) return;
    try {
      setVoiceListeners(await fetchVoiceListeners(voiceId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load voice bookend coverage.');
    }
  };

  const reviewById = useMemo(
    () => new Map(reviews.map((asset) => [asset.asset_id, asset])),
    [reviews],
  );
  const activeAssets = useMemo(
    () => reviews.flatMap((asset) => asset.versions
      .filter((version) => asset.asset_state === 'active' && version.technical_review_state === 'approved')
      .map((version) => ({ asset, version }))),
    [reviews],
  );
  const selectedOption = assignmentOptions.find((option) => option.story_id === selectedStoryId);
  const selectedAsset = activeAssets.find(({ asset }) => asset.asset_id === selectedAssetId);
  const newestCanaryByAssignment = useMemo(() => {
    const result = new Map<string, InternalSoundbedReview>();
    [...canaryRenders]
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .forEach((render) => {
        if (!result.has(render.assignment_id)) result.set(render.assignment_id, render);
      });
    return result;
  }, [canaryRenders]);
  const shouldPollCanaries = queuedAssignmentIds.length > 0 || canaryRenders.some(
    (render) => render.status === 'rendering' || render.status === 'regenerating'
  );

  useEffect(() => {
    if (!shouldPollCanaries) return;
    const timer = window.setInterval(() => {
      void refreshCanaries().catch((err) => setError(
        err instanceof Error ? err.message : 'Unable to refresh canary status.'
      ));
    }, 4000);
    return () => window.clearInterval(timer);
  }, [refreshCanaries, shouldPollCanaries]);

  const queueCanary = async (assignment: SoundbedAssignment) => {
    const key = `assignment:${assignment.assignment_id}:canary`;
    setBusy(key);
    setNotice('');
    setError('');
    try {
      const result = await enqueueInternalSoundbedReview(
        assignment.story_id,
        selectedVoiceId,
        assignment.assignment_id,
        selectedListenerId,
      );
      if (result.status === 'queued') {
        setQueuedAssignmentIds((current) => Array.from(new Set([
          ...current,
          assignment.assignment_id,
        ])));
        setNotice(`${assignment.story_title} was queued. Status will update below.`);
      } else {
        setNotice(`${assignment.story_title} already has an active canary.`);
      }
      await refreshCanaries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not queue canary.');
    } finally {
      setBusy('');
    }
  };

  const run = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key);
    setNotice('');
    setError('');
    try {
      await action();
      setNotice(success);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Soundbed action failed.');
    } finally {
      setBusy('');
    }
  };

  if (!registry) {
    return <section className="soundbed-admin"><h1>Soundbed review</h1><p>{error || 'Loading exact source evidence…'}</p></section>;
  }

  return (
    <section className="soundbed-admin">
      <header className="soundbed-admin__header">
        <div>
          <p className="soundbed-admin__eyebrow">Internal operator only</p>
          <h1>Soundbed review</h1>
          <p>Intake, rights review, and private source verification are separate, content-bound steps. Nothing here enables family rendering.</p>
        </div>
        <div className="soundbed-admin__fingerprint">
          Registry <code>{registry.fingerprint.slice(0, 12)}…</code>
        </div>
      </header>

      <label className="soundbed-admin__reference">
        Reviewed rollout reference
        <input
          value={approvalReference}
          onChange={(event) => setApprovalReference(event.target.value)}
          spellCheck={false}
        />
      </label>

      {(notice || error) && (
        <div className={`soundbed-admin__notice${error ? ' soundbed-admin__notice--error' : ''}`} role="status" aria-live="polite">
          {error || notice}
        </div>
      )}

      <div className="soundbed-admin__grid">
        {registry.assets.map((locked) => (
          <SoundbedCard
            key={locked.asset_id}
            locked={locked}
            review={reviewById.get(locked.asset_id)}
            approvalReference={approvalReference}
            registryFingerprint={registry.fingerprint}
            rightsConfirmed={Boolean(rightsConfirmed[locked.asset_id])}
            busy={busy}
            setRightsConfirmed={(checked) => setRightsConfirmed((current) => ({ ...current, [locked.asset_id]: checked }))}
            run={run}
          />
        ))}
      </div>

      <section className="soundbed-assignment" aria-labelledby="soundbed-assignment-heading">
        <div className="soundbed-assignment__heading">
          <div>
            <p className="soundbed-admin__eyebrow">Separate editorial layer</p>
            <h2 id="soundbed-assignment-heading">Story-performance assignment</h2>
          </div>
          <p>Energy cues come from the exact reviewed performance segments. Staging does not enable family rendering.</p>
        </div>

        <div className="soundbed-assignment__form">
          <label>
            Published story
            <select value={selectedStoryId} onChange={(event) => setSelectedStoryId(event.target.value)}>
              <option value="">Choose a story</option>
              {assignmentOptions.map((option) => (
                <option key={option.story_id} value={option.story_id}>
                  {option.title} · performance v{option.performance_version}
                </option>
              ))}
            </select>
          </label>
          <label>
            Reviewed soundbed
            <select value={selectedAssetId} onChange={(event) => setSelectedAssetId(event.target.value)}>
              <option value="">Choose an approved soundbed</option>
              {activeAssets.map(({ asset, version }) => (
                <option key={`${asset.asset_id}:${version.version}`} value={asset.asset_id}>
                  {asset.title} · source v{version.version}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={busy !== '' || !selectedOption || !selectedAsset}
            onClick={() => selectedOption && selectedAsset && void run(
              'assignment:stage',
              () => stageSoundbedAssignment(
                selectedOption.story_id,
                selectedAsset.asset.asset_id,
                selectedAsset.version.version,
                selectedOption.arc_cues,
              ),
              `${selectedOption.title} was staged for internal soundbed listening review.`,
            )}
          >{busy === 'assignment:stage' ? 'Staging…' : 'Stage exact assignment'}</button>
        </div>

        <div className="soundbed-assignment__list">
          {assignments.length === 0 && <p>No story soundbed assignments have been staged.</p>}
          {assignments.map((assignment) => {
            const canary = newestCanaryByAssignment.get(assignment.assignment_id);
            const awaitingWorker = queuedAssignmentIds.includes(assignment.assignment_id) && !canary;
            const canaryPending = awaitingWorker || canary?.status === 'rendering' || canary?.status === 'regenerating';
            const canaryReady = canary?.status === 'machine_passed' || canary?.status === 'needs_review';
            return (
            <article className="soundbed-assignment__item" key={assignment.assignment_id}>
              <div>
                <h3>{assignment.story_title}</h3>
                <p>{assignment.asset_title} · performance v{assignment.performance_version} · {assignment.mix_profile_version}</p>
                <span className="soundbed-card__state">{assignment.assignment_state}</span>
              </div>
              {assignment.mix_profile_version !== assignment.current_mix_profile_version && (
                <div className="soundbed-assignment__canary">
                  <p>
                    This failed review remains recorded as {assignment.mix_profile_version}.
                    Prepare {assignment.current_mix_profile_version} before another canary.
                  </p>
                  <button
                    type="button"
                    disabled={busy !== '' || assignment.assignment_state === 'approved'}
                    onClick={() => void run(
                      `assignment:${assignment.assignment_id}:revise`,
                      () => reviseSoundbedAssignmentMixProfile(
                        assignment.assignment_id,
                        assignment.mix_profile_version,
                      ),
                      `${assignment.story_title} is ready for a new ${assignment.current_mix_profile_version} listening review.`,
                    )}
                  >{busy === `assignment:${assignment.assignment_id}:revise` ? 'Preparing…' : `Prepare ${assignment.current_mix_profile_version}`}</button>
                </div>
              )}
              {assignment.assignment_state === 'listening_review'
                && assignment.mix_profile_version === assignment.current_mix_profile_version && (
                <div className="soundbed-assignment__canary">
                  <label>
                    Family voice
                    <select value={selectedVoiceId} onChange={(event) => void chooseVoice(event.target.value)}>
                      <option value="">Choose a ready voice</option>
                      {state.voices.filter((voice) => voice.status === 'ready').map((voice) => (
                        <option key={voice.voice_id} value={voice.voice_id}>{voice.voice_name}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Listener with both bookends
                    <select value={selectedListenerId} onChange={(event) => setSelectedListenerId(event.target.value)}>
                      <option value="">Choose a listener</option>
                      {voiceListeners.filter((listener) => listener.bookend_complete).map((listener) => (
                        <option key={listener.listener_id} value={listener.listener_id}>{listener.display_name}</option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    disabled={busy !== '' || !selectedVoiceId || !selectedListenerId || Boolean(canaryPending || canaryReady)}
                    onClick={() => void queueCanary(assignment)}
                  >{busy === `assignment:${assignment.assignment_id}:canary` ? 'Queueing…' : 'Queue internal canary'}</button>
                </div>
              )}
              {(awaitingWorker || canary) && (
                <div className="soundbed-assignment__canary-status" role="status" aria-live="polite">
                  {awaitingWorker && <p>Queued — waiting for the worker to start.</p>}
                  {canaryPending && !awaitingWorker && <p>Rendering private canary… This can take several minutes.</p>}
                  {canaryReady && canary && (
                    <p>Canary ready. <Link to={`/renders/${canary.render_id}`}>Open listening review</Link></p>
                  )}
                  {canary?.status === 'failed' && <p>Canary failed. Open Tonight for failure details or try again.</p>}
                </div>
              )}
            </article>
          )})}
        </div>
      </section>
    </section>
  );
};

interface CardProps {
  locked: LockedSoundbedAsset;
  review?: SoundbedReviewAsset;
  approvalReference: string;
  registryFingerprint: string;
  rightsConfirmed: boolean;
  busy: string;
  setRightsConfirmed: (checked: boolean) => void;
  run: (key: string, action: () => Promise<unknown>, success: string) => Promise<void>;
}

const SoundbedCard: React.FC<CardProps> = ({
  locked,
  review,
  approvalReference,
  registryFingerprint,
  rightsConfirmed,
  busy,
  setRightsConfirmed,
  run,
}) => {
  const lockedVersion = locked.versions[0];
  const reviewedVersion = review?.versions.find((version) => version.version === lockedVersion.version);
  const key = locked.asset_id;
  const canApproveRights = reviewedVersion?.rights_review_state === 'source_verified';
  const canUpload = reviewedVersion?.rights_review_state === 'human_approved'
    && reviewedVersion.technical_review_state === 'pending';

  return (
    <article className="soundbed-card">
      <div className="soundbed-card__title-row">
        <div><h2>{locked.title}</h2><p>by {locked.creator_name}</p></div>
        <span className={`soundbed-card__state soundbed-card__state--${review?.asset_state || 'locked'}`}>
          {review?.asset_state || 'locked candidate'}
        </span>
      </div>

      <dl className="soundbed-card__facts">
        <div><dt>License</dt><dd><a href={lockedVersion.license_url} target="_blank" rel="noreferrer">{lockedVersion.license_spdx_id}</a></dd></div>
        <div><dt>Runtime</dt><dd>{Math.round(lockedVersion.duration_seconds)} sec</dd></div>
        <div><dt>Source level</dt><dd>{lockedVersion.baseline_lufs ?? '—'} LUFS / {lockedVersion.baseline_true_peak_dbtp ?? '—'} dBTP</dd></div>
        <div><dt>Territory</dt><dd>{lockedVersion.territory_scope}</dd></div>
      </dl>

      <p className="soundbed-card__attribution">{lockedVersion.attribution_text}</p>
      <p><a href={lockedVersion.source_page_url} target="_blank" rel="noreferrer">Review original source page ↗</a></p>
      <details>
        <summary>Exact source and authorship evidence</summary>
        <p className="soundbed-card__checksum"><strong>SHA-256</strong> {lockedVersion.source_audio_sha256}</p>
        <pre>{JSON.stringify(lockedVersion.source_evidence, null, 2)}</pre>
      </details>

      {!reviewedVersion && (
        <button
          type="button"
          disabled={busy !== '' || approvalReference.trim().length < 8}
          onClick={() => void run(
            `${key}:intake`,
            () => intakeLockedSoundbed(key, registryFingerprint, approvalReference.trim()),
            `${locked.title} was added as a fail-closed candidate.`,
          )}
        >{busy === `${key}:intake` ? 'Adding…' : 'Intake exact candidate'}</button>
      )}

      {canApproveRights && reviewedVersion && (
        <div className="soundbed-card__review-step">
          <label>
            <input
              type="checkbox"
              checked={rightsConfirmed}
              onChange={(event) => setRightsConfirmed(event.target.checked)}
            />
            I reviewed the exact source, license, authorship evidence, attribution, and territory above.
          </label>
          <button
            type="button"
            disabled={busy !== '' || !rightsConfirmed}
            onClick={() => void run(
              `${key}:rights`,
              () => approveSoundbedRights(key, reviewedVersion.version, reviewedVersion.rights_evidence_sha256),
              `${locked.title} rights evidence was approved.`,
            )}
          >{busy === `${key}:rights` ? 'Approving…' : 'Approve exact rights evidence'}</button>
        </div>
      )}

      {canUpload && reviewedVersion && (
        <div className="soundbed-card__review-step">
          <p>Luma will retrieve the exact registry-pinned source, verify its byte count and SHA-256, and store it privately. You do not need to supply a file.</p>
          <button
            type="button"
            disabled={busy !== ''}
            onClick={() => void run(
              `${key}:source`,
              () => fetchAndApproveSoundbedSource(key, reviewedVersion.version, reviewedVersion.technical_evidence_sha256),
              `${locked.title} exact bytes were verified and stored privately.`,
            )}
          >{busy === `${key}:source` ? 'Retrieving and verifying…' : 'Retrieve exact source and approve technical review'}</button>
        </div>
      )}

      {reviewedVersion?.technical_review_state === 'approved' && (
        <p className="soundbed-card__ready">Rights and exact private source are approved. Assignment remains a separate review.</p>
      )}
    </article>
  );
};

export default SoundbedAdmin;
